/**
 * High-level patterned-polygon layer factory.
 *
 * Wraps Deck's GeoJsonLayer + FillStyleExtension with:
 *   - The #7326 shader workaround (via installNoModInject)
 *   - Mipmapped REPEAT-wrapped textures (via makeMippedTexture)
 *   - Pattern preset lookup + custom canvas support
 *
 * Constraint: one pattern per layer. For N patterns, build N layers.
 */

import {GeoJsonLayer} from '@deck.gl/layers';
import {FillStyleExtension} from '@deck.gl/extensions';

import {installNoModInject} from './fill_style_patch.js';
import {makeMippedTexture, PATTERN_PRESETS, makeDotsCanvas, makeDiagonalStripeCanvas, makeCrosshatchCanvas, makeVerticalStripesCanvas} from './pattern_textures.js';

/**
 * Build a patterned GeoJsonLayer ready to add to a Deck instance or MapboxOverlay.
 *
 * @param {object} opts
 * @param {Device} opts.device      luma.gl device (from Deck.device or overlay._deck.device)
 * @param {string} opts.id          unique Deck layer id
 * @param {object} opts.data        GeoJSON FeatureCollection or array of features
 * @param {string|HTMLCanvasElement} opts.pattern
 *   One of: 'dots' | 'hatch' | 'hatch_back' | 'cross' | 'vstripe'
 *   Or a pre-built canvas. Or a luma.gl Texture (used as-is, no mipmap setup).
 * @param {number[]} [opts.color=[0,0,0,255]] RGBA fill color (alpha matters for overlapping patterns).
 * @param {number[]} [opts.lineColor=[0,0,0,255]] RGBA stroke color.
 * @param {number} [opts.lineWidthMinPixels=1] stroke minimum width in screen pixels.
 * @param {number} [opts.tileWorldMeters=24] desired tile size in real-world meters.
 * @param {boolean} [opts.mask=true] true = mask mode (recolorable); false = image mode (RGB verbatim).
 * @param {string} [opts.beforeId] Mapbox layer id to render below (interleaved mode only).
 * @param {boolean} [opts.pickable=false]
 * @param {Function} [opts.onClick]
 * @param {Function} [opts.onHover]
 * @returns {GeoJsonLayer}
 */
export function createPatternedLayer(opts) {
  if (!opts || !opts.device) {
    throw new Error('PsDeckMaps.createPatternedLayer: opts.device is required');
  }
  if (!opts.id) {
    throw new Error('PsDeckMaps.createPatternedLayer: opts.id is required');
  }

  // Install the #7326 shader fix lazily on first use.
  installNoModInject();

  const {
    device,
    id,
    data,
    pattern,
    color = [0, 0, 0, 255],
    lineColor = [0, 0, 0, 255],
    lineWidthMinPixels = 1,
    tileWorldMeters = 24,
    mask = true,
    beforeId,
    pickable = false,
    onClick,
    onHover,
    stroked = true,
    filled = true
  } = opts;

  // Resolve `pattern` to a luma.gl Texture.
  const texture = resolvePatternTexture(device, pattern);

  // Frame is the whole texture (no atlas sub-rect — that's the constraint).
  const frameSize = texture.width;
  const fillPatternScale = tileWorldMeters / frameSize;

  const layerProps = {
    id,
    data,
    pickable,
    stroked,
    filled,
    lineWidthMinPixels,
    getLineColor: lineColor,
    getFillColor: color,
    fillPatternMask: mask,
    fillPatternAtlas: texture,
    fillPatternMapping: {full: {x: 0, y: 0, width: frameSize, height: frameSize}},
    getFillPattern: () => 'full',
    getFillPatternScale: fillPatternScale,
    getFillPatternOffset: [0, 0],
    extensions: [new FillStyleExtension({pattern: true})]
  };

  if (beforeId) layerProps.beforeId = beforeId;
  if (onClick) layerProps.onClick = onClick;
  if (onHover) layerProps.onHover = onHover;

  return new GeoJsonLayer(layerProps);
}

function resolvePatternTexture(device, pattern) {
  // Already a Texture? Use as-is.
  if (pattern && typeof pattern === 'object' && pattern.handle && pattern.sampler) {
    return pattern;
  }

  // Canvas or image? Upload with mipmaps.
  if (pattern instanceof HTMLCanvasElement || pattern instanceof HTMLImageElement) {
    return makeMippedTexture(device, pattern);
  }

  // Named preset string?
  if (typeof pattern === 'string') {
    const factory = PATTERN_PRESETS[pattern];
    if (!factory) {
      throw new Error(`PsDeckMaps.createPatternedLayer: unknown pattern preset "${pattern}" (known: ${Object.keys(PATTERN_PRESETS).join(', ')})`);
    }
    return makeMippedTexture(device, factory());
  }

  throw new Error('PsDeckMaps.createPatternedLayer: opts.pattern must be a preset name, canvas, image, or Texture');
}

// Re-export raw helpers for callers that need lower-level access.
export {
  makeDotsCanvas,
  makeDiagonalStripeCanvas,
  makeCrosshatchCanvas,
  makeVerticalStripesCanvas,
  makeMippedTexture,
  PATTERN_PRESETS
};
